/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
// Import necessary modules and dependencies
import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TableTypeProvider, SampleTypeProvider, TableService } from '@domain/business-layer';
import {
	DateTimeService,
	FormattingService,
	PlatformName,
	PlatformProvider,
	ToastDuration,
	ToastService
} from '@shared';
import { TableExpandableCards } from '../models';
import { BaseFileExporterService } from './base-file-exporter.service';
import { DataRepository } from '@database/workspace';
import { TimeDataFormattingService } from './time-data-formatting.service';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { TranslateService } from '@ngx-translate/core';
import { ReportFileExporter } from 'report-file-exporter/src';

// Assign pdfFonts to pdfMake's vfs property
pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
	providedIn: 'root'
})
export class PdfFileExporterService extends BaseFileExporterService {
	private isPlatformWeb: boolean;
	private isPlatformIOS: boolean;
	private isPlatformANDROID: boolean;
	public constructor(
		private platformProvider: PlatformProvider,
		protected tableTypeProvider: TableTypeProvider,
		protected sampleTypeProvider: SampleTypeProvider,
		protected tableService: TableService,
		protected dataRepository: DataRepository,
		protected formattingService: FormattingService,
		protected dateTimeService: DateTimeService,
		protected timeDataFormattingService: TimeDataFormattingService,
		private toastService: ToastService,
		private translateService: TranslateService
	) {
		super(
			tableTypeProvider,
			sampleTypeProvider,
			tableService,
			dataRepository,
			formattingService,
			dateTimeService,
			timeDataFormattingService
		);
		this.isPlatformWeb = platformProvider.getPlatform() === PlatformName.WEB;
		this.isPlatformIOS = platformProvider.getPlatform() === PlatformName.IOS;
		this.isPlatformANDROID = platformProvider.getPlatform() === PlatformName.ANDROID;
	}

	// Export data to a PDF file
	async exportData(tablesExpandableCards: TableExpandableCards[], fileName: string): Promise<void> {
		// Initialize PDF content
		const pdfContent = [];
		const pageOrientation = 'landscape';
		const maxColumnsPerPage = 12;
		const numReferenceColumns = 1;
		const maxRowsPerPage = 24;
		// Initialize a flag to track the first table
		let isFirstTable = true;

		// Iterate through each table to export
		for (const tableExpandableCards of tablesExpandableCards) {
			if (!tableExpandableCards.expandableCards || tableExpandableCards.expandableCards.length === 0) {
				continue; // Skip empty tables
			}
			// Use the tableName property for each table
			const tableName = tableExpandableCards.tableName;

			// Extract headers from the first card's properties
			const headers = tableExpandableCards.expandableCards[0].properties.map((property) => property.label || '');
			const columnWidths = headers.map(() => 'auto'); // Auto width for each column

			let startColumn = 0;

			// Create PDF pages with tables
			while (startColumn < headers.length) {
				const isReferencePage = startColumn >= numReferenceColumns;
				let endColumn = isReferencePage
					? Math.min(startColumn + maxColumnsPerPage - numReferenceColumns, headers.length)
					: Math.min(startColumn + maxColumnsPerPage, headers.length);
				let pageColumns = isReferencePage
					? headers.slice(0, numReferenceColumns).concat(headers.slice(startColumn, endColumn))
					: headers.slice(startColumn, endColumn);

				let startRow = 0;
				while (startRow < tableExpandableCards.expandableCards.length) {
					const endRow = Math.min(startRow + maxRowsPerPage, tableExpandableCards.expandableCards.length);
					const tableDefinition = {
						table: {
							widths: columnWidths.slice(0, pageColumns.length),
							body: []
						},
						layout: 'lightHorizontalLines'
					};

					// Add the header row for each new page
					let headerRow = isReferencePage
						? headers.slice(0, numReferenceColumns).concat(headers.slice(startColumn, endColumn))
						: headers.slice(startColumn, endColumn);
					tableDefinition.table.body.push(headerRow);

					// Add rows to the table
					for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
						let rowData = tableExpandableCards.expandableCards[rowIndex].properties.map((property) =>
							property.value !== undefined ? property.value : ''
						);
						if (isReferencePage) {
							rowData = rowData
								.slice(0, numReferenceColumns)
								.concat(rowData.slice(startColumn, endColumn));
						} else {
							rowData = rowData.slice(startColumn, endColumn);
						}
						tableDefinition.table.body.push(rowData);
					}

					// Add a page break before each new table (except the first) and for continued tables
					if (!isFirstTable || startRow > 0 || startColumn > 0) {
						pdfContent.push({ text: '', pageBreak: 'before' });
					}
					pdfContent.push({
						text: `${fileName} - ${tableName}`,
						style: 'tableHeader',
						margin: [0, 0, 0, 10]
					});
					pdfContent.push(tableDefinition);
					startRow = endRow;
				}

				startColumn = endColumn;
			}
			// After processing the first table, set the flag to false
			isFirstTable = false;
		}

		// Remove the page break from the first page (if it exists)
		if (pdfContent.length > 0 && pdfContent[0].pageBreak) {
			pdfContent.shift();
		}

		// Define the document structure and style
		const documentDefinition = {
			content: pdfContent,
			pageOrientation: pageOrientation,
			pageSize: 'A3',
			pageMargins: [40, 40, 40, 40],
			styles: {
				tableHeader: {
					fontSize: 12,
					bold: true,
					alignment: 'center'
				},
				tableCell: {
					fontSize: 12
				}
			},
			defaultStyle: {
				fontSize: 12
			}
		};

		// Create a PDF document generator
		const pdfDocGenerator = pdfMake.createPdf(documentDefinition);


		if (this.isPlatformWeb) {
			try {
				// Generate the PDF as a Blob
				const blob = await new Promise<Blob>((resolve, reject) => {
					pdfDocGenerator.getBlob(
						(blob) => {
							resolve(blob);
						},
						(error) => {
							reject(error);
						}
					);
				});

				// Create a download link for the PDF and trigger the download
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl, '_blank');
				const a = document.createElement('a');
				a.href = blobUrl;
				a.download = fileName + '.pdf';
				a.click();
				URL.revokeObjectURL(blobUrl);
			} catch (error) {
				console.error('Error generating PDF:', error);
			}
		}
		if (this.isPlatformIOS || this.isPlatformANDROID) {
			try {
				const base64data = await new Promise<string>((resolve, reject) => {
					pdfDocGenerator.getBase64(
						(dataUrl) => {
							resolve(dataUrl);
						},
						(error) => {
							reject(error);
						}
					);
				});

				const fileNameWithExt = `${fileName}.pdf`;

				// Write the PDF to the filesystem
				const writeResult = await Filesystem.writeFile({
					path: fileNameWithExt,
					data: base64data,
					directory: this.isPlatformANDROID ? Directory.External : Directory.Documents
				});

				if (writeResult.uri) {
					// Get the file URI to open it
					// For Android: "file:///storage/emulated/0/Android/data/com.datamine.quicklogger/files/Drill%20Hole%20-%202024-05-18%2011%3A11%20PM.pdf"

					const fileUri = writeResult.uri;

					this.toastService.presentSuccess(
						this.translateService.instant('entry.export.successMessages.exportSuccessful'),
						ToastDuration.Slow
					);
				}
			} catch (error) {
				console.log(error);
				this.toastService.presentError(
					this.translateService.instant('entry.export.errorMessages.exportFailed'),
					ToastDuration.Slow
				);
			}
		}
	}

	// Method to export PDF with reference columns
	async exportDataWithReferences(
		tablesExpandableCards,
		fileName,
		numFrozenColumns,
		pageOrientation,
		pageSize,
		userDefinedMaxLength
	) {
		// Initialize PDF content
		const pdfContent = [];
		const maxColumnsPerPage = 10;
		const maxRowsPerPage = 24;
		const maxColumnWidth = getMaxColumnWidth(pageSize, pageOrientation);

		function getMaxColumnWidth(
			pageSize: 'A4' | 'A3' | 'LETTER' | 'LEGAL',
			pageOrientation: 'landscape' | 'portrait'
		): number {
			// Define page dimensions (width x height) in portrait orientation in points
			const pageDimensions: { [key: string]: { width: number; height: number } } = {
				A4: { width: 595, height: 842 },
				A3: { width: 842, height: 1191 },
				Letter: { width: 612, height: 792 },
				Legal: { width: 612, height: 1008 }
			};

			// Get dimensions for the given pageSize
			const dimensions = pageDimensions[pageSize] || pageDimensions['A4']; // Default to A4 if pageSize is unknown

			// Calculate available width based on orientation
			const availableWidth = pageOrientation === 'landscape' ? dimensions.height : dimensions.width;

			// Calculate maxColumnWidth as 1/3 of available space
			const maxColumnWidth = availableWidth / 3;

			return maxColumnWidth;
		}

		function isBase64Image(str) {
			// Check if str is a string and starts with the base64 image prefix
			return typeof str === 'string' && str.startsWith('iVBORw0KGgo');
		}

		// Retrieve the first record's first column value for the header
		const firstRecordValue =
			tablesExpandableCards.length > 0 && tablesExpandableCards[0].expandableCards.length > 0
				? tablesExpandableCards[0].expandableCards[0].properties[0].value
				: 'N/A';

		let isFirstTable = true;



		// Iterate through each table to export
		for (const tableExpandableCards of tablesExpandableCards) {

			const expandableCards = tableExpandableCards.expandableCards

			expandableCards.forEach(card => {
				// Iterate through each property in the properties array
				card.properties.forEach(property => {
					// Check if the valueType is 'TIME24'
					if (property.valueType === 'TIME24' || property.valueType === 'TIME12') {
						property.value = property.value.split("T")[1];
					}
				});
			});

			// modifiy value when value type Time24 or 12
			if (!tableExpandableCards.expandableCards || tableExpandableCards.expandableCards.length === 0) {
				continue; // Skip empty tables
			}

			const tableName = tableExpandableCards.tableName;
			const headers = tableExpandableCards.expandableCards[0].properties.map((property) => property.label || '');
			const columnWidths = [];
			const columnsExceedingMaxLength = [];

			for (const columnIndex in headers) {
				const columnData = tableExpandableCards.expandableCards.map((card) => card.properties[columnIndex]);
				const maxLength = columnData.reduce(
					(max, property) =>
						typeof property.value === 'string' ? Math.max(max, property.value.length) : max,
					0
				);

				let exceedsMaxLength = maxLength > userDefinedMaxLength;
				// Apply width adjustment only for subsequent tables, not the first
				let width = isFirstTable ? 'auto' : exceedsMaxLength ? maxColumnWidth : 'auto';

				columnWidths.push(width);

				if (exceedsMaxLength && !isFirstTable) {
					columnsExceedingMaxLength.push(headers[columnIndex]);
				}
			}

			// After calculating columnWidths
			const columnWidthMap = headers.reduce((acc, header, index) => {
				acc[header] = columnWidths[index];
				return acc;
			}, {});

			if (!isFirstTable) {
				// Insert a page break before starting a new table after the first
				pdfContent.push({ text: '', pageBreak: 'before' });
			}

			if (isFirstTable) {
				pdfContent.push({
					text: `${fileName} - ${tableName} - ${firstRecordValue}`,
					style: 'tableHeader',
					margin: [0, 0, 0, 10]
				});

				// Determine the total number of columns and initialize the starting column index
				let totalColumns = headers.length;
				let startColumnIndex = 0;

				// While there are columns left to process
				while (startColumnIndex < totalColumns) {
					// Determine the end column index for the current set (not exceeding maxColumnsPerPage)
					let endColumnIndex = Math.min(startColumnIndex + maxColumnsPerPage, totalColumns);

					// Prepare headers and column widths for the current set
					let currentSetHeaders = headers.slice(startColumnIndex, endColumnIndex);
					let currentSetColumnWidths = columnWidths.slice(startColumnIndex, endColumnIndex);

					// Initialize the table definition for the current set of columns
					const tableDefinition = {
						table: {
							widths: currentSetColumnWidths,
							body: []
						},
						layout: 'lightHorizontalLines'
					};

					// Add the header row for the current set of columns
					tableDefinition.table.body.push(currentSetHeaders);

					// Add row data for the current set of columns
					let rowData = tableExpandableCards.expandableCards[0].properties
						.slice(startColumnIndex, endColumnIndex)
						.map((property) => {
							if (isBase64Image(property.value)) {
								// Handle base64 encoded images
								const imageData = `data:image/png;base64,${property.value}`;
								return { image: imageData, width: 150, height: 50 };
							}
							return property.value !== undefined ? property.value : '';
						});

					// Add the row data to the table definition
					tableDefinition.table.body.push(rowData);

					// Add the current set of columns to the PDF content
					if (startColumnIndex > 0) {
						// Instead of a page break, insert a visual spacer (text or empty row) for non-first column sets
						pdfContent.push({ text: ' ', margin: [0, 10, 0, 10], style: 'tableHeader' }); // Adjust space as needed
					}
					pdfContent.push(tableDefinition);

					// Update the start column index for the next set of columns
					startColumnIndex = endColumnIndex;
				}

				// Mark the first table as processed
				isFirstTable = false;
			} else {
				// Logic for all other tables with page breaks, focusing on columns first ("over then down")
				let startRow = 0;
				let totalRows = tableExpandableCards.expandableCards.length;

				// Iterate through rows in chunks based on maxRowsPerPage
				while (startRow < totalRows) {
					const endRow = Math.min(startRow + maxRowsPerPage, totalRows);

					let startColumn = 0;
					// Now prioritize completing all columns for the current row subset before moving to the next row subset
					while (startColumn < headers.length) {
						const isReferencePage = startColumn >= numFrozenColumns;
						let endColumn = isReferencePage
							? Math.min(startColumn + maxColumnsPerPage - numFrozenColumns, headers.length)
							: Math.min(startColumn + maxColumnsPerPage, headers.length);
						let pageColumns = isReferencePage
							? headers.slice(0, numFrozenColumns).concat(headers.slice(startColumn, endColumn))
							: headers.slice(startColumn, endColumn);
						let currentSetColumnWidths = pageColumns.map(
							(columnName) => columnWidthMap[columnName] || 'auto'
						);

						const tableDefinition = {
							table: {
								widths: currentSetColumnWidths,
								body: []
							},
							layout: 'lightHorizontalLines'
						};

						// Header for the current column subset
						let headerRow = pageColumns;
						tableDefinition.table.body.push(headerRow);

						// Add rows for the current subset of rows and columns
						for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
							let rowData = tableExpandableCards.expandableCards[rowIndex].properties.map(
								(property, columnIndex) => {
									if (isBase64Image(property.value)) {
										const imageData = `data:image/png;base64,${property.value}`;
										return { image: imageData, width: 150, height: 50 };
									}
									return property.value !== undefined ? property.value : '';
								}
							);
							if (isReferencePage) {
								rowData = rowData
									.slice(0, numFrozenColumns)
									.concat(rowData.slice(startColumn, endColumn));
							} else {
								rowData = rowData.slice(startColumn, endColumn);
							}
							tableDefinition.table.body.push(rowData);
						}

						// Add a page break before each new column set (except the first) and for continued rows
						if (startColumn > 0 || startRow > 0) {
							pdfContent.push({ text: '', pageBreak: 'before' });
						}
						pdfContent.push({
							text: `${fileName} - ${tableName} - ${firstRecordValue}`,
							style: 'tableHeader',
							margin: [0, 0, 0, 10]
						});
						pdfContent.push(tableDefinition);

						startColumn = endColumn; // Move to the next column subset
					}

					startRow = endRow; // Move to the next row subset after completing all columns for the current set
				}
			}
			isFirstTable = false;
		}


		// Remove the page break from the first page (if it exists)
		if (pdfContent.length > 0 && pdfContent[0].pageBreak) {
			pdfContent.shift();
		}

		// Define the document structure and style
		const documentDefinition = {
			content: pdfContent,
			pageOrientation: pageOrientation,
			pageSize: pageSize,
			pageMargins: [40, 40, 40, 40],
			styles: {
				tableHeader: {
					fontSize: 12,
					bold: true,
					alignment: 'center'
				},
				tableCell: {
					fontSize: 12
				}
			},
			defaultStyle: {
				fontSize: 12
			}
		};

		// Create a PDF document generator
		const pdfDocGenerator = pdfMake.createPdf(documentDefinition);

		if (this.isPlatformWeb) {
			try {
				// Generate the PDF as a Blob
				const blob = await new Promise<Blob>((resolve, reject) => {
					pdfDocGenerator.getBlob(
						(blob) => {
							resolve(blob);
						},
						(error) => {
							reject(error);
						}
					);
				});

				// Create a download link for the PDF and trigger the download
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl, '_blank');
				const a = document.createElement('a');
				a.href = blobUrl;
				a.download = fileName + '.pdf';
				a.click();
				URL.revokeObjectURL(blobUrl);
			} catch (error) {
				console.error('Error generating PDF:', error);
			}
		}

		if (this.isPlatformIOS || this.isPlatformANDROID) {
			try {
				const base64data = await new Promise<string>((resolve, reject) => {
					pdfDocGenerator.getBase64(
						(dataUrl) => {
							resolve(dataUrl);
						},
						(error) => {
							reject(error);
						}
					);
				});

				const fileNameWithExt = `${fileName}.pdf`;

				// Write the PDF to the filesystem
				const writeResult = await Filesystem.writeFile({
					path: fileNameWithExt,
					data: base64data,
					directory: this.isPlatformANDROID ? Directory.Cache : Directory.Documents
				});
				const fileURIs: string[] = [];
				if (this.isPlatformANDROID) {

					fileURIs.push(writeResult.uri);
					try {
						await ReportFileExporter.writeReport({
							fileNames: [fileNameWithExt]
						});
					} catch (error) {
						this.toastService.presentError(
							this.translateService.instant('entry.export.errorMessages.exportFailed'),
							ToastDuration.Slow
						);
					}
				}

				if (writeResult.uri) {
					// Get the file URI to open it
					// For Android: "file:///storage/emulated/0/Android/data/com.datamine.quicklogger/files/Drill%20Hole%20-%202024-05-18%2011%3A11%20PM.pdf"

					const fileUri = writeResult.uri;

					this.toastService.presentSuccess(
						this.translateService.instant('entry.export.successMessages.exportSuccessful'),
						ToastDuration.Slow
					);
				}
			} catch (error) {
				console.log(error);
				this.toastService.presentError(
					this.translateService.instant('entry.export.errorMessages.exportFailed'),
					ToastDuration.Slow
				);
			}
		}
	}
}
